import {
  AloftDate,
  AloftPrice,
  RecommendedCompsSources,
  isDefined,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Icon,
  ProductColor,
  Tooltip,
  Typography,
} from '@aloft/toolkit-sui';
import {
  SortingState,
  Table,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Dispatch, ReactNode, SetStateAction, useMemo, useRef } from 'react';

import { getSimilarComparableToSubjectPriorSaleTooltip } from '../../../modules/alerts/definitions/comparable/similarComparableToSubjectPriorSaleUtils.ts';
import {
  LocationType,
  formatProximityRequirementTooltip,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import {
  formatSaleDateRequirementTooltip,
  isSaleDateOutOfBounds,
} from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { PairedTwoLineCell } from '../internal/PairedTwoLineCell.tsx';
import { SimilarityScoreChip } from '../internal/SimilarityScoreChip.tsx';
import { UADRatingsSpinner } from '../internal/gridHelpers/UADRatingsSpinner.tsx';
import { ValueWithWarningGutter } from '../internal/gridHelpers/ValueWithWarningGutter.tsx';
import { WarningTooltipIcon } from '../internal/gridHelpers/WarningTooltipIcon.tsx';
import { addressColumn } from '../internal/gridHelpers/addressColumn.tsx';
import {
  getSortedRowModelWithPinnedSubject,
  getSortedRowModelWithPinnedSubjectAndGroups,
} from '../internal/gridHelpers/getSortedRowModelWithPinnedSubject.ts';
import { rentalStatusColumn } from '../internal/gridHelpers/rentalStatusColumn.tsx';
import { makeSelectionColumn } from '../internal/gridHelpers/selectionColumn.tsx';
import { CompSelectionProperty, CompSelectionState } from '../types.ts';
import { formatPropertyTypeAbbreviation } from '../utils/formatPropertyTypeAbbreviation.ts';
import { RECOMMENDED_COMPS_STATUS_GROUP_ORDER, getStatusGroup } from '../utils/getStatusGroup.ts';

const INITIAL_PAGE_INDEX = 0;
const PAGE_SIZE = 500;

function formatDateCell(date?: { format: (fmt: string) => string }) {
  return date ? date.format('MM/yy') : '';
}

function booleanToYesNo(value?: boolean) {
  if (value === undefined) return '';
  return value ? 'Yes' : 'No';
}

function formatAddressCell(address: CompSelectionProperty['address']) {
  return `${address?.AddressLineText}, ${address?.CityName}, ${address?.StateCode} ${address?.PostalCode}`;
}

function getMlsId(property: CompSelectionProperty): string {
  if (property.id === 'subject') return '';
  if (property.listingData?.ListingId) return property.listingData.ListingId;
  if (!property.listingID) return '';
  return parseToolkitListingID(property.listingID)?.listingID ?? '';
}

function UADRatingCell({
  rating,
  prefix,
  isLoading,
  hasFailed,
  isSkipped,
  failureTooltip,
  commentary,
}: {
  rating?: number;
  prefix: 'Q' | 'C';
  isLoading?: boolean;
  hasFailed?: boolean;
  isSkipped?: boolean;
  failureTooltip: string;
  commentary?: string;
}) {
  const wrap = (content: ReactNode) => (
    <Flex alignItems="center" justifyContent="center" width="100%">
      {content}
    </Flex>
  );

  if (isLoading) {
    return wrap(<UADRatingsSpinner />);
  }

  if (rating !== undefined) {
    const label = `${prefix}${rating}`;
    return wrap(
      commentary ? (
        <Tooltip label={commentary}>
          <Typography __dangerDoNotUseSx={{ cursor: 'help' }}>{label}</Typography>
        </Tooltip>
      ) : (
        label
      ),
    );
  }

  if (isSkipped) {
    return wrap(
      <Tooltip label="UAD ratings image analysis is only available for the first 20 recommended comps.">
        <Typography variant="body2" color="textSecondary" __dangerDoNotUseSx={{ cursor: 'help' }}>
          -
        </Typography>
      </Tooltip>,
    );
  }

  if (hasFailed) {
    return wrap(
      <Tooltip label={failureTooltip}>
        <Typography variant="body2" color="textSecondary">
          ?
        </Typography>
      </Tooltip>,
    );
  }

  return '';
}

function UADRatingHeader({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Flex alignItems="center" gap={1}>
      <Tooltip label={tooltip}>
        <Block __dangerDoNotUseSx={{ cursor: 'help' }}>
          <Icon icon="sparkle" size="md" color="buttonDefault" />
        </Block>
      </Tooltip>
      {label}
    </Flex>
  );
}

function ProximityCell({
  property,
  locationType,
  proximityRequirementMiles,
  unitLabel = 'mi',
  valueMinWidth,
}: {
  property: CompSelectionProperty;
  locationType: LocationType | undefined;
  proximityRequirementMiles: number | undefined;
  unitLabel?: 'mi' | 'miles';
  valueMinWidth?: number;
}) {
  if (property.id === 'subject' || property.distanceToSubject === undefined) return '';

  const distanceText = `${property.distanceToSubject} ${unitLabel}`;
  const showWarning =
    locationType !== undefined &&
    proximityRequirementMiles !== undefined &&
    property.distanceToSubject > proximityRequirementMiles;

  return (
    <ValueWithWarningGutter
      valueMinWidth={valueMinWidth}
      warning={
        showWarning ? (
          <WarningTooltipIcon
            label={formatProximityRequirementTooltip(locationType!, proximityRequirementMiles!)}
          />
        ) : undefined
      }
    >
      <Typography
        variant="body2"
        color="textPrimary"
        __dangerDoNotUseSx={{ textAlign: 'left', width: '100%' }}
      >
        {distanceText}
      </Typography>
    </ValueWithWarningGutter>
  );
}

const PROXIMITY_VALUE_WIDTH_MI = 48;
const PROXIMITY_VALUE_WIDTH_MILES = 72;
const DATE_VALUE_MIN_WIDTH = 36;

export const useRecommendedCompSelectionTable = (
  data: CompSelectionProperty[],
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  sorting: SortingState,
  setSorting: Dispatch<SetStateAction<SortingState>>,
  rowSelection: CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  source: RecommendedCompsSources,
  orderID: string,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
  locationType: LocationType | undefined,
  proximityRequirementMiles: number | undefined,
  effectiveDate: AloftDate | undefined,
  saleDateRequirementMonths: number | undefined,
  compSelectionTableVariant: 'sales' | 'rental' = 'sales',
  imageAnalysisRatingsEnabled: boolean = false,
  subjectPriorSaleDate: AloftDate | undefined = undefined,
  subjectPriorSalePrice: AloftPrice | undefined = undefined,
): {
  table: Table<CompSelectionProperty>;
  rowsToPin: number;
  tableData: CompSelectionProperty[];
} => {
  const tableData = useMemo(() => [subject, ...data], [subject, data]);

  const rowsToPin = 1;

  const rowSelectionRef = useRef<CompSelectionState>(rowSelection);
  rowSelectionRef.current = rowSelection;

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<CompSelectionProperty>();
    const proximityValueMinWidth =
      compSelectionTableVariant === 'rental'
        ? PROXIMITY_VALUE_WIDTH_MILES
        : PROXIMITY_VALUE_WIDTH_MI;

    const [qualityCol, conditionCol] = imageAnalysisRatingsEnabled
      ? [
          columnHelper.accessor((p) => p.uadQualityRating, {
            id: 'Qlty.',
            header: () => (
              <UADRatingHeader
                label="Qlty."
                tooltip="AI Image Analysis recommended UAD quality rating"
              />
            ),
            cell: ({ row: { original } }) => (
              <UADRatingCell
                rating={original.uadQualityRating}
                prefix="Q"
                isLoading={original.uadRatingsLoading}
                hasFailed={original.uadQualityRatingFailed}
                isSkipped={original.uadRatingsSkipped}
                failureTooltip="Failed to analyze the quality rating"
                commentary={original.uadQualityRatingCommentary}
              />
            ),
          }),
          columnHelper.accessor((p) => p.uadConditionRating, {
            id: 'Cond.',
            header: () => (
              <UADRatingHeader
                label="Cond."
                tooltip="AI Image Analysis recommended UAD condition rating"
              />
            ),
            cell: ({ row: { original } }) => (
              <UADRatingCell
                rating={original.uadConditionRating}
                prefix="C"
                isLoading={original.uadRatingsLoading}
                hasFailed={original.uadConditionRatingFailed}
                isSkipped={original.uadRatingsSkipped}
                failureTooltip="Failed to analyze the condition rating"
                commentary={original.uadConditionRatingCommentary}
              />
            ),
          }),
        ]
      : [undefined, undefined];

    if (compSelectionTableVariant === 'rental') {
      return [
        makeSelectionColumn(
          () => rowSelectionRef.current,
          setRowSelection,
          isInspectifyOrder,
          hasArvOption,
        ),
        columnHelper.accessor((p) => p.mlsStatus, {
          id: 'Status',
          cell: ({ table, row }) => {
            const sortedRows = table.getSortedRowModel().rows;
            const visibleIndex = sortedRows.findIndex((r) => r.id === row.id);
            return rentalStatusColumn(row.original, visibleIndex);
          },
        }),
        columnHelper.accessor((p) => formatAddressCell(p.address), {
          id: 'Address',
          cell: ({ row }) => addressColumn(row.original),
        }),
        columnHelper.accessor((p) => p.similarityScore, {
          id: 'Similarity Score',
          cell: ({ row: { original } }) =>
            isDefined(original.similarityScore)
              ? original.similarityScore.toFixed(2).toString()
              : '',
        }),
        columnHelper.accessor((p) => p.distanceToSubject, {
          id: 'Proximity',
          cell: ({ row: { original } }) => (
            <ProximityCell
              property={original}
              locationType={locationType}
              proximityRequirementMiles={proximityRequirementMiles}
              unitLabel="miles"
              valueMinWidth={proximityValueMinWidth}
            />
          ),
        }),
        columnHelper.accessor((p) => p.leaseDate ?? p.saleDate, {
          id: 'Lease Date',
          cell: ({ row: { original } }) => formatDateCell(original.leaseDate ?? original.saleDate),
        }),
        columnHelper.accessor((p) => p.cumulativeDaysOnMarket, {
          id: 'CDOM',
          cell: ({ row: { original } }) =>
            original.cumulativeDaysOnMarket !== undefined
              ? `${original.cumulativeDaysOnMarket} days`
              : '',
        }),
        columnHelper.accessor(
          (p) => p.rentRate?.toNumericPrice() ?? p.listPrice?.toNumericPrice(),
          {
            id: 'Rent Rate',
            cell: ({ row: { original } }) =>
              (original.rentRate ?? original.listPrice)?.toString(true) ?? '',
          },
        ),
        columnHelper.accessor((p) => p.propertyType, {
          id: 'Property Type',
          cell: ({ row }) => row.original.propertyType,
        }),
        columnHelper.accessor((p) => p.yearBuilt, {
          id: 'Year Built',
          cell: ({ row: { original } }) => original.yearBuilt?.toString(),
        }),
        columnHelper.accessor((p) => p.gla?.toSqFt().getUnitAmount(), {
          id: 'GLA',
          cell: ({ row: { original } }) => original.gla?.toString(),
        }),
        columnHelper.accessor((p) => p.lotSize?.toSqFt().getUnitAmount(), {
          id: 'Lot Size',
          cell: ({ row: { original } }) => original.lotSize?.toRoundedString(),
        }),
        columnHelper.accessor((p) => p.rentRate ?? p.listPrice, {
          id: 'Rent / sqft',
          cell: ({ row: { original } }) => {
            const rent = original.rentRate ?? original.listPrice;
            const glaSqft = original.gla?.toSqFt().getUnitAmount();
            return rent && glaSqft ? `${rent.div(glaSqft || 1).toString(true)}/sqft` : '';
          },
        }),
        columnHelper.accessor((p) => p.bedrooms, {
          id: 'Beds',
          cell: ({ row: { original } }) => original.bedrooms?.toString(),
        }),
        columnHelper.accessor((p) => p.fullBaths, {
          id: 'Full Baths',
          cell: ({ row: { original } }) => original.fullBaths?.toString(),
        }),
        columnHelper.accessor((p) => p.halfBaths, {
          id: 'Half Baths',
          cell: ({ row: { original } }) => original.halfBaths?.toString(),
        }),
        columnHelper.accessor((p) => p.garage, {
          id: 'Garage',
          cell: ({ row: { original } }) => booleanToYesNo(original.garage),
        }),
        columnHelper.accessor((p) => p.garageCount, {
          id: 'Garage Count',
          cell: ({ row: { original } }) => original.garageCount?.toString(),
        }),
        columnHelper.accessor((p) => p.basement, {
          id: 'Basement',
          cell: ({ row: { original } }) => booleanToYesNo(original.basement),
        }),
        columnHelper.accessor((p) => p.pool, {
          id: 'Pool',
          cell: ({ row: { original } }) => booleanToYesNo(original.pool ?? false),
        }),
      ].filter(isDefined);
    }

    return [
      makeSelectionColumn(
        () => rowSelectionRef.current,
        setRowSelection,
        isInspectifyOrder,
        hasArvOption,
      ),
      columnHelper.accessor((p) => formatAddressCell(p.address), {
        id: 'Address',
        cell: ({ row }) => addressColumn(row.original, { showPinAndWarnings: true }),
      }),
      columnHelper.accessor((p) => p.similarityScore, {
        id: 'Similarity',
        cell: ({ row: { original } }) => {
          if (!isDefined(original.similarityScore) || original.id === 'subject') return '';
          const similarityPercent = Math.round(original.similarityScore * 100);
          return (
            <Flex alignItems="center" gap="8px">
              <Typography variant="body2">{similarityPercent}</Typography>
              <SimilarityScoreChip score={similarityPercent} />
            </Flex>
          );
        },
      }),
      columnHelper.accessor((p) => p.distanceToSubject, {
        id: 'Proximity',
        cell: ({ row: { original } }) => (
          <ProximityCell
            property={original}
            locationType={locationType}
            proximityRequirementMiles={proximityRequirementMiles}
            valueMinWidth={proximityValueMinWidth}
          />
        ),
      }),
      columnHelper.accessor((p) => p.salePrice?.toNumericPrice(), {
        id: 'Price',
        header: () => <PairedTwoLineCell primary="Sale Price" secondary="List Price" />,
        cell: ({ row: { original } }) => {
          const prices = (
            <PairedTwoLineCell
              primary={original.salePrice?.toString(true) ?? ''}
              secondary={original.listPrice?.toString(true) ?? ''}
            />
          );

          if (original.id === 'subject') return prices;

          const warningLabel = getSimilarComparableToSubjectPriorSaleTooltip(
            original.saleDate,
            original.salePrice,
            subjectPriorSaleDate,
            subjectPriorSalePrice,
          );

          return (
            <ValueWithWarningGutter
              warning={warningLabel ? <WarningTooltipIcon label={warningLabel} /> : undefined}
            >
              {prices}
            </ValueWithWarningGutter>
          );
        },
      }),
      columnHelper.accessor((p) => p.qualityTier, {
        id: 'Q Tier',
        cell: ({ row: { original } }) => {
          if (original.qualityTier === undefined) return '';
          return (
            <Flex alignItems="center" gap="8px">
              <Block
                width="16px"
                height="16px"
                backgroundColor={`qualityTier${original.qualityTier}` as ProductColor}
              />
              Tier {original.qualityTier}
            </Flex>
          );
        },
      }),
      columnHelper.accessor((p) => p.gla?.toSqFt().getUnitAmount(), {
        id: 'GLA',
        cell: ({ row: { original } }) => original.gla?.toSqFt().toString(true),
      }),
      qualityCol,
      conditionCol,
      columnHelper.accessor((p) => p.propertyType, {
        id: 'Type',
        cell: ({ row: { original } }) => formatPropertyTypeAbbreviation(original.propertyType),
      }),
      columnHelper.accessor((p) => p.yearBuilt, {
        id: 'Year',
        cell: ({ row: { original } }) => original.yearBuilt?.toString(),
      }),
      columnHelper.accessor((p) => p.lotSize?.toSqFt().getUnitAmount(), {
        id: 'Site Size',
        cell: ({ row: { original } }) => original.lotSize?.toSqFt().toRoundedString(true),
      }),
      columnHelper.accessor((p) => p.saleDate, {
        id: 'Date',
        header: () => <PairedTwoLineCell primary="Sale Date" secondary="Contract Date" />,
        cell: ({ row: { original } }) => {
          const dates = (
            <PairedTwoLineCell
              primary={formatDateCell(original.saleDate)}
              secondary={formatDateCell(original.contractDate)}
            />
          );

          if (original.id === 'subject') {
            return (
              <ValueWithWarningGutter valueMinWidth={DATE_VALUE_MIN_WIDTH}>
                {dates}
              </ValueWithWarningGutter>
            );
          }

          const showWarning =
            locationType !== undefined &&
            isSaleDateOutOfBounds(original.saleDate, effectiveDate, saleDateRequirementMonths);

          return (
            <ValueWithWarningGutter
              valueMinWidth={DATE_VALUE_MIN_WIDTH}
              warning={
                showWarning && saleDateRequirementMonths !== undefined ? (
                  <WarningTooltipIcon
                    label={formatSaleDateRequirementTooltip(
                      locationType!,
                      saleDateRequirementMonths,
                    )}
                  />
                ) : undefined
              }
            >
              {dates}
            </ValueWithWarningGutter>
          );
        },
      }),
      columnHelper.accessor((p) => p.cumulativeDaysOnMarket, {
        id: 'CDOM',
        cell: ({ row: { original } }) => original.cumulativeDaysOnMarket?.toString(),
      }),
      columnHelper.accessor((p) => p.bedrooms, {
        id: 'Beds',
        cell: ({ row: { original } }) => original.bedrooms?.toString(),
      }),
      columnHelper.accessor((p) => p.fullBaths, {
        id: 'Baths Full',
        cell: ({ row: { original } }) => original.fullBaths?.toString(),
      }),
      columnHelper.accessor((p) => p.halfBaths, {
        id: 'Baths Half',
        cell: ({ row: { original } }) => original.halfBaths?.toString(),
      }),
      columnHelper.accessor((p) => p.garageCount, {
        id: 'Garage',
        cell: ({ row: { original } }) => original.garageCount?.toString(),
      }),
      columnHelper.accessor((p) => p.basement, {
        id: 'Basement',
        cell: ({ row: { original } }) => booleanToYesNo(original.basement),
      }),
      columnHelper.accessor((p) => p.pool, {
        id: 'Pool',
        cell: ({ row: { original } }) => booleanToYesNo(original.pool ?? false),
      }),
      columnHelper.accessor((p) => getMlsId(p), {
        id: 'MLS ID',
        cell: ({ row: { original } }) => {
          const mlsId = getMlsId(original);
          return mlsId ? `#${mlsId}` : '';
        },
      }),
    ].filter(isDefined);
  }, [
    compSelectionTableVariant,
    comps.length,
    hasArvOption,
    imageAnalysisRatingsEnabled,
    isInspectifyOrder,
    locationType,
    orderID,
    proximityRequirementMiles,
    effectiveDate,
    saleDateRequirementMonths,
    subjectPriorSaleDate,
    subjectPriorSalePrice,
    rowsToPin,
    setRowSelection,
    source,
  ]);

  const getSortedRowModelFn = useMemo(
    () =>
      compSelectionTableVariant === 'sales'
        ? getSortedRowModelWithPinnedSubjectAndGroups(
            rowsToPin,
            (row: CompSelectionProperty) => getStatusGroup(row.mlsStatus),
            RECOMMENDED_COMPS_STATUS_GROUP_ORDER,
          )
        : getSortedRowModelWithPinnedSubject(rowsToPin),
    [compSelectionTableVariant, rowsToPin],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    enableSorting: true,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModelFn,
    getPaginationRowModel: getPaginationRowModel(),
    state: { sorting },
    initialState: {
      pagination: {
        pageIndex: INITIAL_PAGE_INDEX,
        pageSize: PAGE_SIZE,
      },
    },
  });

  return { table, rowsToPin, tableData };
};
